import React, { useState, useEffect, useRef } from "react"
import ReactDOM from "react-dom"

// ── Design tokens ─────────────────────────────────────────────────────────────
const FF   = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO = "'IBM Plex Mono', monospace"

const T = {
  border:       "0.5px solid #e5e7eb",
  radiusCard:   10,
  bg:           "#fff",
  bgMuted:      "#fafaf9",
  text:         "#111827",
  textMuted:    "#374151",
  textTertiary: "#6b7280",
  shadow:       "0 4px 16px rgba(0,0,0,0.08)",
}

const generateTimeOptions = (interval) => {
  const options = []
  const base = new Date()
  base.setHours(0, 0, 0, 0)
  for (let i = 0; i < 24 * 60; i += interval) {
    const t = new Date(base.getTime() + i * 60000)
    const h12 = (t.getHours() % 12 || 12).toString().padStart(2, "0")
    const min = t.getMinutes().toString().padStart(2, "0")
    const period = t.getHours() < 12 ? "AM" : "PM"
    const h24 = t.getHours().toString().padStart(2, "0")
    options.push({
      time24: `${h24}:${min}`,
      display: `${h12}:${min} ${period}`,
      h12, min, period,
    })
  }
  return options
}

const TIMES = generateTimeOptions(15)

const CustomTimePicker = ({ value = "", onChange, placeholder = "--:-- --" }) => {
  const [open, setOpen]       = useState(false)
  const [pos, setPos]         = useState({ top: 0, left: 0, width: 0 })
  const triggerRef            = useRef(null)
  const listRef               = useRef(null)
  const selectedRef           = useRef(null)

  // Compute portal position from trigger's bounding rect
  const openDropdown = () => {
    if (!triggerRef.current) return
    const r = triggerRef.current.getBoundingClientRect()
    const dropH = 208 // max-height of list
    const spaceAbove = r.top
    const spaceBelow = window.innerHeight - r.bottom

    // Prefer upward; fall back to downward if not enough room
    if (spaceAbove >= dropH || spaceAbove > spaceBelow) {
      setPos({ bottom: window.innerHeight - r.top + 4, top: undefined, left: r.left, width: r.width })
    } else {
      setPos({ top: r.bottom + 4, bottom: undefined, left: r.left, width: r.width })
    }
    setOpen(true)
  }

  // Scroll selected into view after portal mounts
  useEffect(() => {
    if (open && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "center", behavior: "instant" })
    }
  }, [open])

  // Close on outside click or Escape
  useEffect(() => {
    const onDown = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        listRef.current   && !listRef.current.contains(e.target)
      ) setOpen(false)
    }
    const onKey = (e) => { if (e.key === "Escape") setOpen(false) }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [])

  const [timeStr, period] = value ? value.split(" ") : ["", ""]

  return (
    <>
      {/* ── Trigger ── */}
      <button
        type="button"
        ref={triggerRef}
        onClick={() => open ? setOpen(false) : openDropdown()}
        style={{
          width: "100%", height: "100%",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 10px",
          background: open ? T.bgMuted : T.bg,
          border: "none", outline: "none",
          cursor: "pointer",
          transition: "background 0.12s",
          fontFamily: FF,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = T.bgMuted }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = T.bg }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="2" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" />
            <polyline points="12,6 12,12 16,14" />
          </svg>
          {value ? (
            <span style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: MONO }}>{timeStr}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.textTertiary, fontFamily: MONO, letterSpacing: "0.05em" }}>{period}</span>
            </span>
          ) : (
            <span style={{ fontSize: 13, fontWeight: 500, color: "#d1d5db", fontFamily: MONO }}>{placeholder}</span>
          )}
        </div>
        <svg
          width="11" height="11" viewBox="0 0 24 24" fill="none"
          stroke={T.textTertiary} strokeWidth="2.5"
          style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
        >
          <polyline points="6,9 12,15 18,9" />
        </svg>
      </button>

      {/* ── Portal dropdown — renders at document.body, never clipped ── */}
      {open && ReactDOM.createPortal(
        <div
          ref={listRef}
          style={{
            position: "fixed",
            zIndex: 99999,
            top:    pos.top    !== undefined ? pos.top    : undefined,
            bottom: pos.bottom !== undefined ? pos.bottom : undefined,
            left:   pos.left,
            width:  pos.width,
            background: T.bg,
            border: T.border,
            borderRadius: T.radiusCard,
            boxShadow: T.shadow,
            overflow: "hidden",
          }}
        >
          <div style={{ maxHeight: 208, overflowY: "auto", scrollbarWidth: "thin", scrollbarColor: "#e5e7eb #fafaf9" }}>
            {TIMES.map((t, i) => {
              const isSel = t.display === value
              return (
                <div
                  key={i}
                  ref={isSel ? selectedRef : null}
                  onClick={() => { onChange?.(t.time24, t.display); setOpen(false) }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "8px 12px",
                    background: isSel ? "#f9f9f7" : "transparent",
                    cursor: "pointer",
                    borderBottom: i < TIMES.length - 1 ? T.border : "none",
                    transition: "background 0.1s",
                    fontFamily: MONO,
                  }}
                  onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = T.bgMuted }}
                  onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = "transparent" }}
                >
                  <span style={{ fontSize: 13, fontWeight: isSel ? 700 : 500, color: T.text }}>
                    {t.h12}:{t.min}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: "0.06em",
                    color: isSel ? T.textMuted : T.textTertiary,
                    border: T.border, borderRadius: 4, padding: "1px 5px",
                  }}>
                    {t.period}
                  </span>
                </div>
              )
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

export default CustomTimePicker